/**
 * Admin Salesforce-lookup settings client — talks to /api/admin-salesforce.php.
 * Super-admin only. The consumer secret is write-only (never returned).
 */

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

export interface SalesforceSettings {
  sfEnabled: boolean;
  sfLoginUrl: string;
  sfApiVersion: string;
  sfClientId: string;
  sfClientSecretSet: boolean;
  /** Enabled AND has a key + secret. */
  sfConfigured: boolean;
}

export interface SalesforceSettingsPatch {
  sfEnabled?: boolean;
  sfLoginUrl?: string;
  sfApiVersion?: string;
  sfClientId?: string;
  /** Only sent when the admin typed a new one. */
  sfClientSecret?: string;
}

export interface SalesforceTestResult {
  ok: boolean;
  instanceUrl?: string;
  apiVersion?: string;
  error?: string;
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function fetchSalesforceSettings(): Promise<SalesforceSettings> {
  const res = await fetch(`${API_BASE}/admin-salesforce.php`, { credentials: 'include' });
  return (await handle<{ settings: SalesforceSettings }>(res)).settings;
}

export async function saveSalesforceSettings(patch: SalesforceSettingsPatch): Promise<SalesforceSettings> {
  const res = await fetch(`${API_BASE}/admin-salesforce.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ settings: patch }),
  });
  return (await handle<{ settings: SalesforceSettings }>(res)).settings;
}

/** Mints a token with the given (possibly unsaved) settings and reads Account + Contact. */
export async function testSalesforceConnection(patch: SalesforceSettingsPatch): Promise<SalesforceTestResult> {
  try {
    const res = await fetch(`${API_BASE}/admin-salesforce.php`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'test', settings: patch }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body.error || `Request failed (${res.status})` };
    return body as SalesforceTestResult;
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'Network error' };
  }
}
